import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Home, LogOut, Building2, CheckCircle, Clock, FileText, Upload,
  Loader2, Phone, Mail, User, PoundSterling, Eye, Calendar, ThumbsUp,
  ThumbsDown, AlertCircle, ClipboardCheck, MessageSquare, X, Check,
  FileCheck, Pen, ChevronRight, Bell
} from "lucide-react";
import { API } from "@/App";
import FeedbackList from "@/components/feedback/FeedbackList";

// Milestone display component
const MilestoneItem = ({ label, completed, date, current }) => (
  <div className={`flex items-center gap-3 p-4 rounded-lg border ${
    completed ? "bg-green-50 border-green-200" : 
    current ? "bg-blue-50 border-blue-200" :
    "bg-slate-50 border-slate-200"
  }`}>
    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
      completed ? "bg-green-500 text-white" : 
      current ? "bg-blue-500 text-white" :
      "bg-slate-200 text-slate-400"
    }`}>
      {completed ? <CheckCircle className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
    </div>
    <div className="flex-1">
      <p className={`font-medium ${
        completed ? "text-green-700" : 
        current ? "text-blue-700" :
        "text-slate-500"
      }`}>{label}</p>
      {date && <p className="text-xs text-slate-500">{new Date(date).toLocaleDateString('en-GB')}</p>}
      {current && !completed && <p className="text-xs text-blue-600 font-medium">Current Stage</p>}
    </div>
  </div>
);

// Todo item display component
const TodoItem = ({ item, onAction }) => (
  <div className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
    item.completed 
      ? "bg-green-50 border-green-200" 
      : "bg-white border-slate-200 hover:border-emerald-300 hover:shadow-sm"
  }`}>
    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
      item.completed ? "bg-green-500 text-white" : "bg-slate-100 text-slate-400"
    }`}>
      {item.completed ? <Check className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
    </div>
    <div className="flex-1 min-w-0">
      <p className={`font-medium ${item.completed ? "text-green-700" : "text-slate-800"}`}>
        {item.label}
      </p>
      <p className="text-sm text-slate-500 mt-0.5">{item.description}</p>
      {item.uploaded_at && (
        <p className="text-xs text-green-600 mt-1">
          Completed: {new Date(item.uploaded_at).toLocaleDateString('en-GB')}
        </p>
      )}
      {item.signed_at && (
        <p className="text-xs text-green-600 mt-1">
          Signed: {new Date(item.signed_at).toLocaleDateString('en-GB')}
        </p>
      )}
    </div>
    {!item.completed && (
      <Button 
        size="sm" 
        onClick={() => onAction(item)}
        className="flex-shrink-0"
        data-testid={`todo-action-${item.key}`}
      >
        {item.key === "contract_signed" ? (
          <><Pen className="w-4 h-4 mr-1" /> Sign</>
        ) : item.key === "particulars_reviewed" ? (
          <><Eye className="w-4 h-4 mr-1" /> Review</>
        ) : (
          <><Upload className="w-4 h-4 mr-1" /> Upload</>
        )}
      </Button>
    )}
  </div>
);

// Contract Documents Tab Component
const ContractDocumentsTab = ({ selectedProperty, token }) => {
  const [contractData, setContractData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchContractDetails = async () => {
      if (!selectedProperty?.case?.case_id && !selectedProperty?.instruction?.instruction_id) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const caseId = selectedProperty?.instruction?.instruction_id || selectedProperty?.case?.case_id;
        const response = await axios.get(`${API}/portal/seller/contract/${caseId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setContractData(response.data);
      } catch (error) {
        console.error("Error fetching contract details:", error);
        setContractData(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchContractDetails();
  }, [selectedProperty, token]);

  const handleDownloadContract = async () => {
    if (!contractData?.token && !selectedProperty?.instruction?.instruction_id) return;
    
    setDownloading(true);
    try {
      const caseId = selectedProperty?.instruction?.instruction_id || selectedProperty?.case?.case_id;
      const response = await axios.get(`${API}/portal/seller/contract/${caseId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Contract_${selectedProperty?.case?.address_line1 || 'Property'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Contract downloaded successfully");
    } catch (error) {
      console.error("Error downloading contract:", error);
      toast.error(error.response?.data?.detail || "Failed to download contract");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-600" />
          Your Contract
        </CardTitle>
        <CardDescription>
          View and download your signed agency contract
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!contractData?.has_contract ? (
          <div className="text-center py-8 bg-slate-50 rounded-lg">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-700">No Contract Yet</h3>
            <p className="text-slate-500 mt-1 max-w-md mx-auto">
              Your agency contract hasn&apos;t been sent yet. Once it&apos;s ready, you&apos;ll receive an email with a link to review and accept it.
            </p>
          </div>
        ) : (
          <>
            {/* Contract Status Card */}
            <div className={`p-6 rounded-lg border-2 ${
              contractData.status === "accepted" 
                ? "bg-emerald-50 border-emerald-200" 
                : contractData.status === "pending"
                ? "bg-amber-50 border-amber-200"
                : "bg-slate-50 border-slate-200"
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {contractData.status === "accepted" ? (
                    <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                  ) : contractData.status === "pending" ? (
                    <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-slate-400 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                  )}
                  <div>
                    <h3 className={`text-lg font-semibold ${
                      contractData.status === "accepted" ? "text-emerald-800" :
                      contractData.status === "pending" ? "text-amber-800" :
                      "text-slate-700"
                    }`}>
                      {contractData.status === "accepted" ? "Contract Accepted" :
                       contractData.status === "pending" ? "Awaiting Your Acceptance" :
                       contractData.status === "sent" ? "Contract Sent" :
                       "Contract Status"}
                    </h3>
                    <p className="text-sm text-slate-600 mt-0.5">
                      {contractData.property_address}
                    </p>
                  </div>
                </div>
                <Badge className={
                  contractData.status === "accepted" ? "bg-emerald-100 text-emerald-700" :
                  contractData.status === "pending" ? "bg-amber-100 text-amber-700" :
                  "bg-slate-100 text-slate-700"
                }>
                  {contractData.status?.toUpperCase()}
                </Badge>
              </div>
            </div>

            {/* Contract Details */}
            <div className="grid md:grid-cols-2 gap-4">
              {contractData.sent_at && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-500">Sent On</p>
                  <p className="font-medium text-slate-800">
                    {new Date(contractData.sent_at).toLocaleDateString('en-GB', {
                      day: 'numeric', month: 'long', year: 'numeric'
                    })}
                  </p>
                </div>
              )}
              
              {contractData.accepted_at && (
                <div className="bg-emerald-50 rounded-lg p-4">
                  <p className="text-sm text-emerald-600">Accepted On</p>
                  <p className="font-medium text-emerald-800">
                    {new Date(contractData.accepted_at).toLocaleDateString('en-GB', {
                      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
              
              {contractData.accepted_by && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-500">Accepted By</p>
                  <p className="font-medium text-slate-800">{contractData.accepted_by}</p>
                  {contractData.accepted_email && (
                    <p className="text-xs text-slate-500">{contractData.accepted_email}</p>
                  )}
                </div>
              )}
              
              {contractData.audit_trail?.ip_address && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-500">Digital Signature Record</p>
                  <p className="text-xs text-slate-600 font-mono">
                    IP: {contractData.audit_trail.ip_address}
                  </p>
                </div>
              )}
            </div>

            {/* Download Button */}
            {contractData.status === "accepted" && contractData.pdf_available && (
              <div className="pt-4 border-t">
                <Button
                  onClick={handleDownloadContract}
                  disabled={downloading}
                  className="w-full md:w-auto bg-emerald-600 hover:bg-emerald-700"
                  data-testid="download-contract-btn"
                >
                  {downloading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Download Signed Contract (PDF)
                    </>
                  )}
                </Button>
                <p className="text-xs text-slate-500 mt-2">
                  This is your legally binding agency agreement. Keep it for your records.
                </p>
              </div>
            )}

            {/* Pending Status - Link to Accept */}
            {contractData.status === "pending" && contractData.token && (
              <div className="pt-4 border-t">
                <p className="text-amber-700 text-sm mb-3">
                  Please check your email for the contract acceptance link, or click below to review and accept.
                </p>
                <Button
                  onClick={() => window.open(`/contract/accept/${contractData.token}`, '_blank')}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="review-contract-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Review & Accept Contract
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export const SellerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [todoList, setTodoList] = useState(null);
  const [todoProgress, setTodoProgress] = useState({ completed: 0, total: 0, percentage: 0 });
  const [pendingViewings, setPendingViewings] = useState([]);
  const [offers, setOffers] = useState([]);
  const [activeTab, setActiveTab] = useState("todo");
  const [marketingFeedback, setMarketingFeedback] = useState("");
  
  // Sales Instruction state
  const [salesInstruction, setSalesInstruction] = useState(null);
  const [siLoading, setSiLoading] = useState(true);
  
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  
  // Modals
  const [signContractModal, setSignContractModal] = useState(false);
  const [viewingModal, setViewingModal] = useState(false);
  const [offerModal, setOfferModal] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadItem, setUploadItem] = useState(null);
  const [selectedViewing, setSelectedViewing] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Form state
  const [signature, setSignature] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [viewingResponse, setViewingResponse] = useState({ notes: "" });
  const [offerResponse, setOfferResponse] = useState({ action: "", notes: "", counter_amount: "" });
  
  const token = localStorage.getItem("token") || localStorage.getItem("portal_token");
  const user = JSON.parse(localStorage.getItem("user") || localStorage.getItem("portal_client") || "{}");

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/portal/seller/notifications`, { headers });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [token]);

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/portal/seller/notifications/${notificationId}/read`, {}, { headers });
      fetchNotifications();
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  // Mark all as read
  const markAllRead = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/portal/seller/notifications/mark-all-read`, {}, { headers });
      fetchNotifications();
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Error marking all read:", error);
    }
  };

  // Fetch Sales Instruction data
  const fetchSalesInstruction = useCallback(async () => {
    try {
      setSiLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/sales-instructions/seller/my-instruction`, { headers });
      setSalesInstruction(response.data);
    } catch (error) {
      // 404 means no instruction assigned - this is normal
      if (error.response?.status !== 404) {
        console.error("Error fetching sales instruction:", error);
      }
      setSalesInstruction(null);
    } finally {
      setSiLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchDashboard();
    fetchNotifications();
    fetchSalesInstruction();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [token, fetchNotifications, fetchSalesInstruction]);

  const fetchDashboard = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/portal/seller/dashboard`, { headers });
      
      if (response.data.properties?.length > 0) {
        setProperties(response.data.properties || []);
        const prop = response.data.properties[0];
        setSelectedProperty(prop);
        fetchPropertyData(prop.case?.case_id);
      } else {
        // No traditional sales cases - check for Sales Instructions
        try {
          const siResponse = await axios.get(`${API}/sales-instructions/seller/my-instruction`, { headers });
          if (siResponse.data?.instruction) {
            const inst = siResponse.data.instruction;
            // Fetch SI viewings
            const siViewingsRes = await axios.get(`${API}/sales-instructions/seller/my-viewings`, { headers }).catch(() => null);
            const siFeedbackRes = await axios.get(`${API}/sales-instructions/seller/my-feedback`, { headers }).catch(() => null);
            const siOffersRes = await axios.get(`${API}/sales-instructions/seller/my-offers`, { headers }).catch(() => null);
            
            // Convert to property format expected by dashboard
            const siProperty = {
              instruction: inst,
              case: null, // No sales case
              viewings: siViewingsRes?.data?.viewings || [],
              pending_viewings: siViewingsRes?.data?.pending || [],
              feedback: siFeedbackRes?.data?.feedback || [],
              offers: siOffersRes?.data?.offers || [],
              pending_offers: siOffersRes?.data?.pending || [],
              si_mode: true // Flag to indicate this is an SI property
            };
            setProperties([siProperty]);
            setSelectedProperty(siProperty);
            setPendingViewings(siViewingsRes?.data?.pending || []);
            setOffers(siOffersRes?.data?.offers || []);
            setPendingOffers(siOffersRes?.data?.pending || []);
          }
        } catch (siError) {
          console.log("No SI instruction found either");
        }
      }
    } catch (error) {
      console.error("Error fetching seller dashboard:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyData = async (caseId) => {
    if (!caseId) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [todoRes, viewingsRes, allViewingsRes, offersRes] = await Promise.all([
        axios.get(`${API}/portal/seller/todo/${caseId}`, { headers }).catch(() => null),
        axios.get(`${API}/portal/seller/viewings/${caseId}/pending`, { headers }).catch(() => null),
        axios.get(`${API}/portal/seller/viewings/${caseId}`, { headers }).catch(() => null),
        axios.get(`${API}/portal/seller/offers/${caseId}`, { headers }).catch(() => null),
      ]);
      
      if (todoRes?.data) {
        setTodoList(todoRes.data.todo);
        setTodoProgress(todoRes.data.progress);
      }
      if (viewingsRes?.data) {
        setPendingViewings(viewingsRes.data.viewings || []);
      }
      if (allViewingsRes?.data) {
        // Update selectedProperty with all viewings including feedback
        setSelectedProperty(prev => ({
          ...prev,
          viewings: allViewingsRes.data.viewings || [],
          viewings_stats: allViewingsRes.data.stats
        }));
      }
      if (offersRes?.data) {
        setOffers(offersRes.data.offers || []);
      }
    } catch (error) {
      console.error("Error fetching property data:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("portal_token");
    localStorage.removeItem("portal_client");
    navigate("/login");
  };

  const handleSignContract = async () => {
    if (!agreedToTerms || !signature) {
      toast.error("Please agree to terms and provide your signature");
      return;
    }
    setActionLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/portal/seller/todo/${selectedProperty.case.case_id}/sign-contract`,
        { signature, agreed: true },
        { headers }
      );
      toast.success(response.data.message || "Contract signed successfully! Your agent has been notified.");
      setSignContractModal(false);
      setSignature("");
      setAgreedToTerms(false);
      fetchPropertyData(selectedProperty.case.case_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sign contract");
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewingResponse = async (approved) => {
    if (!selectedViewing) return;
    setActionLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/portal/seller/viewings/${selectedViewing.viewing_id}/respond`,
        { approved, notes: viewingResponse.notes },
        { headers }
      );
      toast.success(approved ? "Viewing approved!" : "Viewing declined");
      setViewingModal(false);
      setSelectedViewing(null);
      setViewingResponse({ notes: "" });
      fetchPropertyData(selectedProperty.case.case_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to respond");
    } finally {
      setActionLoading(false);
    }
  };

  const handleOfferResponse = async () => {
    if (!selectedOffer || !offerResponse.action) {
      toast.error("Please select a response");
      return;
    }
    setActionLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/portal/seller/offers/${selectedOffer.offer_id}/respond`,
        {
          action: offerResponse.action,
          notes: offerResponse.notes,
          counter_amount: offerResponse.action === "counter" ? parseFloat(offerResponse.counter_amount) : null
        },
        { headers }
      );
      toast.success("Response submitted!");
      setOfferModal(false);
      setSelectedOffer(null);
      setOfferResponse({ action: "", notes: "", counter_amount: "" });
      fetchPropertyData(selectedProperty.case.case_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to respond");
    } finally {
      setActionLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !uploadItem) {
      toast.error("Please select a file");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', uploadItem.key);
      formData.append('case_id', selectedProperty.case.case_id);
      
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      };
      
      await axios.post(
        `${API}/portal/seller/upload-document`,
        formData,
        { headers }
      );
      
      toast.success(`${uploadItem.label} uploaded successfully!`);
      setUploadModal(false);
      setUploadItem(null);
      setSelectedFile(null);
      fetchPropertyData(selectedProperty.case.case_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  const pendingOffers = offers.filter(o => o.status === "pending" || o.status === "submitted");
  const hasPendingActions = pendingViewings.length > 0 || pendingOffers.length > 0 || (todoProgress.percentage < 100);

  // Format notification time
  const formatNotificationTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB');
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-black text-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/goodacres-full-logo.jpg" 
                alt="Goodacres" 
                className="h-12 w-auto rounded"
              />
              <div className="border-l border-slate-600 pl-4">
                <h1 className="font-semibold text-lg">Seller Portal</h1>
                <p className="text-sm text-slate-400">Welcome, {user?.name || 'Seller'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Notification Bell */}
              <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="relative border-slate-600 text-white hover:bg-slate-800"
                    data-testid="notification-bell"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="end">
                  <div className="border-b p-3 flex items-center justify-between bg-slate-50">
                    <h3 className="font-semibold text-slate-800">Notifications</h3>
                    {unreadCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={markAllRead}
                        className="text-xs text-emerald-600 hover:text-emerald-700"
                      >
                        Mark all read
                      </Button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-slate-500">
                        <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        <p className="text-sm">No notifications yet</p>
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <div
                          key={notif.notification_id}
                          className={`p-3 border-b cursor-pointer transition-colors ${
                            !notif.read ? "bg-emerald-50" : "hover:bg-slate-50"
                          }`}
                          onClick={() => {
                            markNotificationRead(notif.notification_id);
                            // Navigate based on notification type
                            if (notif.type === "new_offer") {
                              setActiveTab("offers");
                            } else if (notif.type === "new_viewing" || notif.type === "viewing_feedback") {
                              setActiveTab("viewings");
                            }
                            setNotificationsOpen(false);
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              notif.type === "new_offer" ? "bg-emerald-100 text-emerald-600" :
                              notif.type === "new_viewing" ? "bg-blue-100 text-blue-600" :
                              "bg-amber-100 text-amber-600"
                            }`}>
                              {notif.type === "new_offer" ? <PoundSterling className="w-4 h-4" /> :
                               notif.type === "new_viewing" ? <Eye className="w-4 h-4" /> :
                               <MessageSquare className="w-4 h-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${!notif.read ? "text-slate-800" : "text-slate-600"}`}>
                                {notif.title}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                                {notif.message}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                {formatNotificationTime(notif.created_at)}
                              </p>
                            </div>
                            {!notif.read && (
                              <div className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0 mt-2" />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="border-slate-600 text-white hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Sales Instruction Card - Shows if seller has an assigned instruction */}
        {!siLoading && salesInstruction && (
          <Card 
            className="mb-6 bg-slate-900 border-slate-700 text-white overflow-hidden cursor-pointer hover:bg-slate-800 transition-all hover:shadow-lg hover:shadow-slate-900/50"
            onClick={() => navigate("/portal/seller/instruction")}
            data-testid="sales-instruction-card"
          >
            <CardContent className="p-0">
              <div className="flex items-center">
                <div className="flex-1 p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Home className="w-5 h-5 text-emerald-400" />
                    <h3 className="font-semibold text-lg">Your Property Sale</h3>
                    {/* Status Badge with notification dot */}
                    {(() => {
                      const instruction = salesInstruction.instruction;
                      const contract = salesInstruction.contract;
                      const brochure = salesInstruction.brochure;
                      
                      // Determine status and if action needed
                      let statusText = "";
                      let statusColor = "";
                      let needsAction = false;
                      
                      if (instruction?.portal_listing_enabled) {
                        statusText = "Property Live";
                        statusColor = "bg-emerald-500";
                      } else if (brochure?.status === "APPROVED" || brochure?.status === "OVERRIDE_APPROVED") {
                        statusText = "Ready for Marketing";
                        statusColor = "bg-blue-500";
                      } else if (brochure?.status === "SENT" || brochure?.status === "PENDING_APPROVAL") {
                        statusText = "Brochure Awaiting Approval";
                        statusColor = "bg-amber-500";
                        needsAction = true;
                      } else if (contract?.status === "SIGNED") {
                        statusText = "Contract Signed";
                        statusColor = "bg-emerald-500";
                      } else if (contract?.status === "SENT") {
                        statusText = "Awaiting Contract";
                        statusColor = "bg-amber-500";
                        needsAction = true;
                      } else {
                        statusText = "In Progress";
                        statusColor = "bg-slate-500";
                      }
                      
                      return (
                        <Badge className={`${statusColor} text-white relative`}>
                          {statusText}
                          {needsAction && (
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                          )}
                        </Badge>
                      );
                    })()}
                  </div>
                  <p className="text-slate-300 text-sm mb-3">
                    {salesInstruction.instruction?.address_line_1}
                    {salesInstruction.instruction?.town && `, ${salesInstruction.instruction?.town}`}
                  </p>
                  <p className="text-slate-400 text-sm">
                    View your contract, approve brochure, and upload documents
                  </p>
                </div>
                <div className="pr-6 flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-slate-400 text-xs">Price</p>
                    <p className="text-xl font-bold text-white">
                      {salesInstruction.instruction?.price_display || `£${(salesInstruction.instruction?.price_amount || 0).toLocaleString()}`}
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    className="border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-white"
                    data-testid="view-property-btn"
                  >
                    View Property
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {properties.length === 0 && !salesInstruction ? (
          <Card className="text-center py-16">
            <CardContent>
              <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h2 className="text-2xl font-semibold text-slate-700">No Active Sales</h2>
              <p className="text-slate-500 mt-2 max-w-md mx-auto">
                You don&apos;t have any active property sales linked to your account yet. 
                Once your property is listed, your sale details will appear here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Action Required Banner */}
            {hasPendingActions && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-800">Action Required</h3>
                    <p className="text-sm text-amber-700">
                      {todoProgress.percentage < 100 && `Complete your to-do list (${todoProgress.completed}/${todoProgress.total}). `}
                      {pendingViewings.length > 0 && `${pendingViewings.length} viewing request(s) pending. `}
                      {pendingOffers.length > 0 && `${pendingOffers.length} offer(s) to review.`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Property Card */}
            {selectedProperty && (
              <Card className="overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-emerald-100 text-sm">Your Property</p>
                        {properties.length > 1 && (
                          <select
                            className="bg-emerald-800 text-white text-xs px-2 py-0.5 rounded border border-emerald-500 cursor-pointer"
                            value={selectedProperty.case?.case_id || selectedProperty.instruction?.id}
                            onChange={(e) => {
                              const prop = properties.find(p => (p.case?.case_id || p.instruction?.id) === e.target.value);
                              if (prop) {
                                setSelectedProperty(prop);
                                if (prop.case?.case_id) {
                                  fetchPropertyData(prop.case?.case_id);
                                }
                              }
                            }}
                            data-testid="property-selector"
                          >
                            {properties.map((p) => (
                              <option key={p.case?.case_id || p.instruction?.id} value={p.case?.case_id || p.instruction?.id}>
                                {p.case?.address_line1 || p.instruction?.address_line_1} {p.case?.postcode || p.instruction?.postcode}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      <h2 className="text-2xl font-bold">{selectedProperty.case?.address_line1 || selectedProperty.case?.property_address?.split(',')[0] || selectedProperty.instruction?.address_line_1 || 'Property'}</h2>
                      <p className="text-emerald-100">{selectedProperty.case?.postcode || selectedProperty.case?.property_address?.split(',').pop()?.trim() || selectedProperty.instruction?.postcode}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-emerald-100 text-sm">Asking Price</p>
                      <p className="text-3xl font-bold">
                        £{(selectedProperty.case?.asking_price || selectedProperty.case?.price || selectedProperty.instruction?.price_amount || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Main Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-5 lg:grid-cols-9">
                <TabsTrigger value="todo" className="relative" data-testid="tab-todo">
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                  To-Do
                  {todoProgress.percentage < 100 && (
                    <Badge className="ml-2 bg-amber-500 text-white">{todoProgress.total - todoProgress.completed}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="calendar" data-testid="tab-calendar">
                  <Calendar className="w-4 h-4 mr-2" />
                  Calendar
                </TabsTrigger>
                <TabsTrigger value="viewings" className="relative" data-testid="tab-viewings">
                  <Eye className="w-4 h-4 mr-2" />
                  Viewings
                  {pendingViewings.length > 0 && (
                    <Badge className="ml-2 bg-blue-500 text-white">{pendingViewings.length}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="offers" className="relative" data-testid="tab-offers">
                  <PoundSterling className="w-4 h-4 mr-2" />
                  Offers
                  {pendingOffers.length > 0 && (
                    <Badge className="ml-2 bg-emerald-500 text-white">{pendingOffers.length}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="feedback" data-testid="tab-feedback">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Feedback
                </TabsTrigger>
                <TabsTrigger value="progress" data-testid="tab-progress">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Progress
                </TabsTrigger>
                <TabsTrigger value="documents" data-testid="tab-documents" className="relative">
                  <FileText className="w-4 h-4 mr-2" />
                  Contract
                  {selectedProperty?.instruction?.contract_accepted && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full" />
                  )}
                </TabsTrigger>
                <TabsTrigger value="ids" data-testid="tab-ids" className="relative">
                  <Upload className="w-4 h-4 mr-2" />
                  IDs
                  {!selectedProperty?.instruction?.ids_uploaded && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full" />
                  )}
                </TabsTrigger>
                <TabsTrigger value="marketing" data-testid="tab-marketing" className="relative">
                  <FileCheck className="w-4 h-4 mr-2" />
                  Marketing
                  {selectedProperty?.instruction?.marketing_sent_at && !selectedProperty?.instruction?.marketing_approved && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" />
                  )}
                </TabsTrigger>
              </TabsList>

              {/* To-Do List Tab */}
              <TabsContent value="todo" className="mt-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <ClipboardCheck className="w-5 h-5 text-emerald-600" />
                          Your To-Do List
                        </CardTitle>
                        <CardDescription>Complete these items to get your property ready for market</CardDescription>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-emerald-600">{todoProgress.percentage}%</p>
                        <p className="text-sm text-slate-500">Complete</p>
                      </div>
                    </div>
                    <Progress value={todoProgress.percentage} className="mt-4" />
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {todoList?.items?.map((item) => (
                      <TodoItem 
                        key={item.key} 
                        item={item} 
                        onAction={(item) => {
                          if (item.key === "contract_signed") {
                            setSignContractModal(true);
                          } else if (item.key === "particulars_reviewed") {
                            navigate(`/portal/seller/particulars/${selectedProperty.case.case_id}`);
                          } else {
                            // Open upload modal for document uploads
                            setUploadItem(item);
                            setSelectedFile(null);
                            setUploadModal(true);
                          }
                        }}
                      />
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Calendar Tab */}
              <TabsContent value="calendar" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      Viewing Calendar
                    </CardTitle>
                    <CardDescription>See all your scheduled, pending, and completed viewings</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Calendar Legend */}
                    <div className="flex items-center gap-6 mb-6 p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                        <span className="text-sm text-slate-600">Pending</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <span className="text-sm text-slate-600">Confirmed</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <span className="text-sm text-slate-600">Completed</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <span className="text-sm text-slate-600">Cancelled</span>
                      </div>
                    </div>

                    {/* Calendar Grid */}
                    {(() => {
                      const allViewings = selectedProperty?.viewings || [];
                      const today = new Date();
                      const currentMonth = today.getMonth();
                      const currentYear = today.getFullYear();
                      
                      // Get days in month
                      const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
                      const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
                      
                      // Create calendar grid
                      const days = [];
                      for (let i = 0; i < firstDayOfMonth; i++) {
                        days.push(null);
                      }
                      for (let d = 1; d <= daysInMonth; d++) {
                        days.push(d);
                      }
                      
                      // Group viewings by date
                      const viewingsByDate = {};
                      allViewings.forEach(v => {
                        const date = v.viewing_date;
                        if (!viewingsByDate[date]) viewingsByDate[date] = [];
                        viewingsByDate[date].push(v);
                      });
                      
                      const monthName = today.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
                      
                      return (
                        <div>
                          <h3 className="text-lg font-semibold text-center mb-4">{monthName}</h3>
                          <div className="grid grid-cols-7 gap-1">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                              <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
                                {day}
                              </div>
                            ))}
                            {days.map((day, idx) => {
                              if (day === null) {
                                return <div key={`empty-${idx}`} className="h-24 bg-slate-50 rounded"></div>;
                              }
                              
                              const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                              const dayViewings = viewingsByDate[dateStr] || [];
                              const isToday = day === today.getDate();
                              
                              return (
                                <div 
                                  key={day} 
                                  className={`h-24 border rounded p-1 overflow-hidden ${isToday ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}
                                >
                                  <div className={`text-xs font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-slate-600'}`}>
                                    {day}
                                  </div>
                                  <div className="space-y-0.5">
                                    {dayViewings.slice(0, 2).map((v, i) => (
                                      <div 
                                        key={i}
                                        className={`text-xs px-1 py-0.5 rounded truncate ${
                                          v.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                          v.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                                          v.status === 'completed' ? 'bg-green-100 text-green-700' :
                                          v.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                          'bg-slate-100 text-slate-700'
                                        }`}
                                        title={`${v.viewer_name || v.applicant_name} - ${v.viewing_time}`}
                                      >
                                        {v.viewing_time} {v.viewer_name || v.applicant_name}
                                      </div>
                                    ))}
                                    {dayViewings.length > 2 && (
                                      <div className="text-xs text-slate-400 px-1">
                                        +{dayViewings.length - 2} more
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Upcoming Viewings List */}
                    <div className="mt-8">
                      <h4 className="font-semibold text-slate-700 mb-4">Upcoming Viewings</h4>
                      {(() => {
                        const allViewings = selectedProperty?.viewings || [];
                        const today = new Date().toISOString().split('T')[0];
                        const upcoming = allViewings.filter(v => 
                          v.viewing_date >= today && 
                          (v.status === 'pending' || v.status === 'confirmed')
                        ).sort((a, b) => a.viewing_date.localeCompare(b.viewing_date));
                        
                        if (upcoming.length === 0) {
                          return (
                            <div className="text-center py-8 bg-slate-50 rounded-lg">
                              <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                              <p className="text-slate-500">No upcoming viewings</p>
                            </div>
                          );
                        }
                        
                        return (
                          <div className="space-y-3">
                            {upcoming.map((v, i) => (
                              <div key={i} className="flex items-center gap-4 p-3 border rounded-lg">
                                <div className={`w-12 h-12 rounded-lg flex flex-col items-center justify-center ${
                                  v.status === 'pending' ? 'bg-amber-100' : 'bg-blue-100'
                                }`}>
                                  <span className="text-xs font-medium text-slate-500">
                                    {new Date(v.viewing_date).toLocaleDateString('en-GB', { month: 'short' })}
                                  </span>
                                  <span className="text-lg font-bold text-slate-800">
                                    {new Date(v.viewing_date).getDate()}
                                  </span>
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium text-slate-800">{v.viewer_name || v.applicant_name}</p>
                                  <p className="text-sm text-slate-500">{v.viewing_time}</p>
                                </div>
                                <Badge className={
                                  v.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                                }>
                                  {v.status}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Viewings Tab */}
              <TabsContent value="viewings" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Eye className="w-5 h-5 text-blue-600" />
                      Viewing Requests
                    </CardTitle>
                    <CardDescription>Approve or decline viewing requests for your property</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {pendingViewings.length === 0 ? (
                      <div className="text-center py-8">
                        <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No pending viewing requests</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {pendingViewings.map((viewing) => (
                          <div 
                            key={viewing.viewing_id}
                            className="p-4 border rounded-lg bg-white hover:shadow-sm transition-shadow"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold">{viewing.viewer_name || viewing.applicant_name}</p>
                                <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    {viewing.viewing_date || viewing.requested_date}
                                  </span>
                                  <span>{viewing.viewing_time || viewing.requested_time}</span>
                                </div>
                                {viewing.notes && (
                                  <p className="text-sm text-slate-600 mt-2 italic">&quot;{viewing.notes}&quot;</p>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedViewing(viewing);
                                    setViewingModal(true);
                                  }}
                                  data-testid={`respond-viewing-${viewing.viewing_id}`}
                                >
                                  Respond
                                  <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* All viewings history with feedback */}
                    {selectedProperty?.viewings?.length > 0 && (
                      <div className="mt-8">
                        <h4 className="font-semibold text-slate-700 mb-4">Viewing History & Feedback</h4>
                        <div className="space-y-3">
                          {selectedProperty.viewings.map((v, i) => (
                            <div key={i} className="p-4 bg-slate-50 rounded-lg border">
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <p className="font-medium">{v.viewer_name}</p>
                                  <p className="text-sm text-slate-500">
                                    {v.viewing_date} at {v.viewing_time}
                                  </p>
                                </div>
                                <Badge className={
                                  v.status === "completed" ? "bg-green-100 text-green-700" :
                                  v.status === "confirmed" ? "bg-blue-100 text-blue-700" :
                                  v.status === "cancelled" ? "bg-red-100 text-red-700" :
                                  "bg-slate-100 text-slate-700"
                                }>
                                  {v.status?.replace(/_/g, " ")}
                                </Badge>
                              </div>
                              
                              {/* Feedback Section */}
                              {v.feedback && (
                                <div className="mt-3 p-3 bg-white rounded-lg border-l-4 border-emerald-500">
                                  <div className="flex items-center gap-2 mb-2">
                                    <MessageSquare className="w-4 h-4 text-emerald-600" />
                                    <span className="font-medium text-emerald-700">Buyer Feedback</span>
                                    {v.feedback_rating && (
                                      <div className="flex items-center gap-1 ml-auto">
                                        {[1,2,3,4,5].map((star) => (
                                          <span 
                                            key={star}
                                            className={star <= v.feedback_rating ? "text-yellow-500" : "text-slate-300"}
                                          >
                                            ★
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-600 italic">"{v.feedback}"</p>
                                </div>
                              )}
                              
                              {v.status === "completed" && !v.feedback && (
                                <p className="text-sm text-slate-400 mt-2 italic">Awaiting feedback...</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Offers Tab */}
              <TabsContent value="offers" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PoundSterling className="w-5 h-5 text-emerald-600" />
                      Offers on Your Property
                    </CardTitle>
                    <CardDescription>Review and respond to offers from potential buyers</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {offers.length === 0 ? (
                      <div className="text-center py-8">
                        <PoundSterling className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No offers received yet</p>
                        <p className="text-sm text-slate-400 mt-1">We&apos;ll notify you when an offer comes in</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {offers.map((offer) => (
                          <div 
                            key={offer.offer_id}
                            className={`p-5 border-2 rounded-lg transition-all ${
                              offer.status === "pending" || offer.status === "submitted"
                                ? "border-emerald-300 bg-emerald-50"
                                : offer.status === "seller_accepted"
                                ? "border-green-500 bg-green-50"
                                : "border-slate-200 bg-white"
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-3 mb-2">
                                  <p className="text-2xl font-bold text-slate-800">
                                    {offer.display_amount}
                                  </p>
                                  <Badge className={
                                    offer.status === "pending" || offer.status === "submitted" ? "bg-amber-100 text-amber-700" :
                                    offer.status === "seller_accepted" ? "bg-green-100 text-green-700" :
                                    offer.status === "rejected" ? "bg-red-100 text-red-700" :
                                    offer.status === "countered" ? "bg-purple-100 text-purple-700" :
                                    "bg-slate-100 text-slate-700"
                                  }>
                                    {offer.status?.replace(/_/g, " ").toUpperCase()}
                                  </Badge>
                                </div>
                                <p className="font-medium text-slate-700">{offer.buyer_name || offer.applicant_name}</p>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {offer.is_cash_buyer && (
                                    <Badge variant="outline" className="text-emerald-700 border-emerald-300">
                                      Cash Buyer
                                    </Badge>
                                  )}
                                  {offer.chain_status && (
                                    <Badge variant="outline">
                                      {offer.chain_status.replace(/_/g, " ")}
                                    </Badge>
                                  )}
                                  {offer.mortgage_agreed && (
                                    <Badge variant="outline" className="text-blue-700 border-blue-300">
                                      Mortgage Agreed
                                    </Badge>
                                  )}
                                </div>
                                {offer.conditions && (
                                  <p className="text-sm text-slate-600 mt-3 p-2 bg-white rounded border">
                                    <strong>Conditions:</strong> {offer.conditions}
                                  </p>
                                )}
                                <p className="text-xs text-slate-400 mt-2">
                                  Received: {new Date(offer.created_at).toLocaleDateString('en-GB')}
                                </p>
                              </div>
                              
                              {(offer.status === "pending" || offer.status === "submitted") && (
                                <Button
                                  onClick={() => {
                                    setSelectedOffer(offer);
                                    setOfferModal(true);
                                  }}
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  data-testid={`respond-offer-${offer.offer_id}`}
                                >
                                  Respond
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Feedback Tab */}
              <TabsContent value="feedback" className="mt-6">
                <FeedbackList siMode={selectedProperty?.si_mode} />
              </TabsContent>

              {/* Sale Progress Tab */}
              <TabsContent value="progress" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      Sale Progress
                    </CardTitle>
                    <CardDescription>
                      Track every step of your property sale in real-time
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {selectedProperty?.case?.case_id ? (
                      <div className="text-center py-8">
                        <div className="w-20 h-20 mx-auto mb-6 bg-emerald-100 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-10 h-10 text-emerald-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-slate-800 mb-2">
                          View Your Sale Progress
                        </h3>
                        <p className="text-slate-500 mb-6 max-w-md mx-auto">
                          Track milestones, view all parties involved, and stay updated on your sale progress in real-time.
                        </p>
                        <Button
                          size="lg"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => navigate(`/portal/seller/progression/${selectedProperty.case.case_id}`)}
                          data-testid="view-progression-btn"
                        >
                          <Eye className="w-5 h-5 mr-2" />
                          Open Sales Progression
                        </Button>
                        <p className="text-xs text-slate-400 mt-4">
                          View milestones, parties, and key dates
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-slate-700">Sale Progress Will Appear Here</h3>
                        <p className="text-slate-500 mt-2">
                          Once you accept an offer, you&apos;ll be able to track the progress of your sale here.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Summary */}
                {selectedProperty?.case?.case_id && (
                  <Card className="mt-6">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Quick Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
                          <p className="text-sm text-emerald-600 font-medium">Property</p>
                          <p className="font-semibold text-slate-800 mt-1">
                            {selectedProperty.case.address_line1}
                          </p>
                        </div>
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-center">
                          <p className="text-sm text-blue-600 font-medium">Sale Price</p>
                          <p className="font-semibold text-slate-800 mt-1">
                            £{(selectedProperty.case.offer_price || selectedProperty.case.asking_price || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 text-center">
                          <p className="text-sm text-purple-600 font-medium">Status</p>
                          <p className="font-semibold text-slate-800 mt-1 capitalize">
                            {selectedProperty.case.status?.replace(/_/g, ' ') || 'Active'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
              
              {/* Contract Documents Tab */}
              <TabsContent value="documents" className="mt-6">
                <ContractDocumentsTab 
                  selectedProperty={selectedProperty}
                  token={token}
                />
              </TabsContent>
              
              {/* IDs Upload Tab */}
              <TabsContent value="ids" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Upload className="w-5 h-5 text-amber-600" />
                      ID Verification
                    </CardTitle>
                    <CardDescription>
                      Please upload your ID documents to proceed with the contract. We need photo ID and proof of address.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {selectedProperty?.instruction?.ids_uploaded ? (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
                        <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-emerald-800">ID Verification Complete</h3>
                        <p className="text-emerald-600 mt-1">Thank you! Your ID documents have been verified.</p>
                      </div>
                    ) : (
                      <>
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Photo ID Upload */}
                          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 hover:border-emerald-400 transition-colors">
                            <h4 className="font-semibold text-slate-800 mb-2">Photo ID</h4>
                            <p className="text-sm text-slate-500 mb-4">Upload your passport or driving licence</p>
                            <Input
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file || !selectedProperty?.instruction?.instruction_id) return;
                                
                                const formData = new FormData();
                                formData.append('file', file);
                                formData.append('doc_type', 'passport');
                                
                                try {
                                  await axios.post(
                                    `${API}/instructions/${selectedProperty.instruction.instruction_id}/upload-id`,
                                    formData,
                                    { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }}
                                  );
                                  toast.success('Photo ID uploaded successfully');
                                  fetchPortalData();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to upload');
                                }
                              }}
                              data-testid="photo-id-upload"
                            />
                          </div>
                          
                          {/* Proof of Address Upload */}
                          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 hover:border-emerald-400 transition-colors">
                            <h4 className="font-semibold text-slate-800 mb-2">Proof of Address</h4>
                            <p className="text-sm text-slate-500 mb-4">Upload utility bill or bank statement (dated within 3 months)</p>
                            <Input
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file || !selectedProperty?.instruction?.instruction_id) return;
                                
                                const formData = new FormData();
                                formData.append('file', file);
                                formData.append('doc_type', 'proof_of_address');
                                
                                try {
                                  await axios.post(
                                    `${API}/instructions/${selectedProperty.instruction.instruction_id}/upload-id`,
                                    formData,
                                    { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }}
                                  );
                                  toast.success('Proof of address uploaded successfully');
                                  fetchPortalData();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to upload');
                                }
                              }}
                              data-testid="proof-address-upload"
                            />
                          </div>
                        </div>
                        
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <p className="text-amber-800 text-sm">
                            <strong>Note:</strong> We cannot send your sales contract until we have received your ID verification documents. 
                            This is a legal requirement for anti-money laundering compliance.
                          </p>
                        </div>
                      </>
                    )}
                    
                    {/* Uploaded Documents List */}
                    {selectedProperty?.instruction?.id_documents?.length > 0 && (
                      <div className="mt-6">
                        <h4 className="font-medium text-slate-700 mb-3">Uploaded Documents</h4>
                        <div className="space-y-2">
                          {selectedProperty.instruction.id_documents.map((doc, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-slate-400" />
                                <div>
                                  <p className="text-sm font-medium">{doc.type.replace('_', ' ')}</p>
                                  <p className="text-xs text-slate-500">Uploaded {new Date(doc.uploaded_at).toLocaleDateString('en-GB')}</p>
                                </div>
                              </div>
                              <CheckCircle className="w-5 h-5 text-emerald-500" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Marketing Approval Tab */}
              <TabsContent value="marketing" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileCheck className="w-5 h-5 text-blue-600" />
                      Marketing Details
                    </CardTitle>
                    <CardDescription>
                      Review and approve the marketing details for your property
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {selectedProperty?.instruction?.marketing_approved || selectedProperty?.case?.marketing_approved ? (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
                        <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-emerald-800">Marketing Approved</h3>
                        <p className="text-emerald-600 mt-1">
                          Approved on {(selectedProperty?.instruction?.marketing_approved_at || selectedProperty?.case?.marketing_approved_at)
                            ? new Date(selectedProperty?.instruction?.marketing_approved_at || selectedProperty?.case?.marketing_approved_at).toLocaleDateString('en-GB')
                            : 'recently'}
                        </p>
                      </div>
                    ) : (selectedProperty?.instruction?.marketing_sent || selectedProperty?.case?.is_instruction) ? (
                      <div className="space-y-6">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <p className="text-blue-800 text-sm font-medium">Marketing details are ready for your review</p>
                        </div>
                        
                        {/* Marketing Preview */}
                        <div className="space-y-4">
                          {/* Property Summary */}
                          {(selectedProperty?.instruction?.summary || selectedProperty?.case?.summary) && (
                            <div>
                              <h4 className="font-medium text-slate-700 mb-2">Property Headline</h4>
                              <p className="text-slate-600 bg-slate-50 p-4 rounded-lg font-medium">
                                {selectedProperty?.instruction?.summary || selectedProperty?.case?.summary}
                              </p>
                            </div>
                          )}
                          
                          {/* Description */}
                          <div>
                            <h4 className="font-medium text-slate-700 mb-2">Property Description</h4>
                            <p className="text-slate-600 bg-slate-50 p-4 rounded-lg whitespace-pre-wrap">
                              {selectedProperty?.instruction?.description || selectedProperty?.case?.description || 'No description provided'}
                            </p>
                          </div>
                          
                          {/* Key Features */}
                          {(selectedProperty?.instruction?.key_features?.length > 0 || selectedProperty?.case?.key_features?.length > 0) && (
                            <div>
                              <h4 className="font-medium text-slate-700 mb-2">Key Features</h4>
                              <ul className="grid grid-cols-2 gap-2 bg-slate-50 p-4 rounded-lg">
                                {(selectedProperty?.instruction?.key_features || selectedProperty?.case?.key_features || []).map((f, i) => (
                                  <li key={i} className="flex items-center gap-2 text-slate-600">
                                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                                    {f}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Photos */}
                          {(selectedProperty?.instruction?.photos?.length > 0 || selectedProperty?.case?.photos?.length > 0) && (
                            <div>
                              <h4 className="font-medium text-slate-700 mb-2">Photos ({(selectedProperty?.instruction?.photos || selectedProperty?.case?.photos || []).length})</h4>
                              <div className="grid grid-cols-4 gap-2">
                                {(selectedProperty?.instruction?.photos || selectedProperty?.case?.photos || []).slice(0, 8).map((photo, i) => (
                                  <img 
                                    key={i}
                                    src={photo.url?.startsWith('http') ? photo.url : `${API}${photo.url}`} 
                                    alt={`Property photo ${i + 1}`}
                                    className="w-full h-20 object-cover rounded-lg"
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Price Info */}
                          <div className="bg-slate-50 p-4 rounded-lg">
                            <h4 className="font-medium text-slate-700 mb-2">Listing Price</h4>
                            <p className="text-2xl font-bold text-slate-800">
                              £{(selectedProperty?.instruction?.price_amount || selectedProperty?.case?.price || selectedProperty?.case?.marketing_price || selectedProperty?.case?.asking_price || 0).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        
                        {/* Request Changes Input */}
                        <div>
                          <Label htmlFor="marketing-feedback">Feedback / Change Requests (optional)</Label>
                          <Textarea
                            id="marketing-feedback"
                            placeholder="Let us know if you'd like any changes to the description, photos, or details..."
                            value={marketingFeedback}
                            onChange={(e) => setMarketingFeedback(e.target.value)}
                            className="mt-1"
                            rows={3}
                            data-testid="marketing-feedback-input"
                          />
                        </div>
                        
                        {/* Approval Buttons */}
                        <div className="flex gap-4 pt-4 border-t">
                          <Button
                            variant="outline"
                            className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                            onClick={async () => {
                              if (!marketingFeedback.trim()) {
                                toast.error('Please provide feedback on what changes you need');
                                return;
                              }
                              setActionLoading(true);
                              try {
                                const instructionId = selectedProperty?.instruction?.instruction_id || selectedProperty?.case?.instruction_id;
                                await axios.post(
                                  `${API}/instructions/${instructionId}/marketing/approve`,
                                  { approved: false, feedback: marketingFeedback },
                                  { headers: { Authorization: `Bearer ${token}` }}
                                );
                                toast.success('Change request sent to your agent');
                                setMarketingFeedback('');
                                fetchPortalData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to submit feedback');
                              } finally {
                                setActionLoading(false);
                              }
                            }}
                            disabled={actionLoading}
                            data-testid="request-changes-btn"
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Request Changes
                          </Button>
                          <Button
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                            onClick={async () => {
                              setActionLoading(true);
                              try {
                                const instructionId = selectedProperty?.instruction?.instruction_id || selectedProperty?.case?.instruction_id;
                                await axios.post(
                                  `${API}/instructions/${instructionId}/marketing/approve`,
                                  { approved: true },
                                  { headers: { Authorization: `Bearer ${token}` }}
                                );
                                toast.success('Marketing details approved!');
                                fetchPortalData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to approve');
                              } finally {
                                setActionLoading(false);
                              }
                            }}
                            disabled={actionLoading}
                            data-testid="approve-marketing-btn"
                          >
                            {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ThumbsUp className="w-4 h-4 mr-2" />}
                            Approve Marketing Details
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">Marketing details are being prepared</p>
                        <p className="text-sm text-slate-400 mt-1">You'll be notified when they're ready for review</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </main>

      {/* Sign Contract Modal - Updated to redirect to full contract page */}
      <Dialog open={signContractModal} onOpenChange={setSignContractModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-emerald-600" />
              Sign Sales Contract
            </DialogTitle>
            <DialogDescription>
              Review and sign your property instruction contract
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-4 rounded-lg text-sm text-slate-600">
              <p className="font-semibold mb-2">What you'll need to do:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Review the property instruction contract</li>
                <li>Confirm commission and fee details</li>
                <li>Agree to 4 declarations</li>
                <li>Provide your electronic signature</li>
              </ul>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>Tip:</strong> Make sure you have a few minutes to complete the process. You can review all terms before signing.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSignContractModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                setSignContractModal(false);
                navigate('/portal/seller/contract');
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="go-to-contract"
            >
              <Pen className="w-4 h-4 mr-2" />
              Review & Sign Contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Viewing Response Modal */}
      <Dialog open={viewingModal} onOpenChange={setViewingModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Respond to Viewing Request</DialogTitle>
            <DialogDescription>
              {selectedViewing?.viewer_name || selectedViewing?.applicant_name} has requested to view your property
            </DialogDescription>
          </DialogHeader>
          
          {selectedViewing && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-slate-700">
                  <Calendar className="w-5 h-5" />
                  <span className="font-medium">
                    {selectedViewing.viewing_date || selectedViewing.requested_date} at {selectedViewing.viewing_time || selectedViewing.requested_time}
                  </span>
                </div>
              </div>
              
              <div>
                <Label htmlFor="viewing-notes">Notes (optional)</Label>
                <Textarea
                  id="viewing-notes"
                  placeholder="Any special instructions or notes..."
                  value={viewingResponse.notes}
                  onChange={(e) => setViewingResponse({ ...viewingResponse, notes: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleViewingResponse(false)}
              disabled={actionLoading}
              className="flex-1"
              data-testid="decline-viewing"
            >
              <ThumbsDown className="w-4 h-4 mr-2" />
              Decline
            </Button>
            <Button
              onClick={() => handleViewingResponse(true)}
              disabled={actionLoading}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              data-testid="approve-viewing"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ThumbsUp className="w-4 h-4 mr-2" />}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offer Response Modal */}
      <Dialog open={offerModal} onOpenChange={setOfferModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Respond to Offer</DialogTitle>
            <DialogDescription>
              Review the offer and choose how you&apos;d like to respond
            </DialogDescription>
          </DialogHeader>
          
          {selectedOffer && (
            <div className="space-y-4 py-4">
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <p className="text-sm text-emerald-700 mb-1">Offer Amount</p>
                <p className="text-3xl font-bold text-emerald-800">{selectedOffer.display_amount}</p>
                <p className="text-sm text-slate-600 mt-2">From: {selectedOffer.buyer_name || selectedOffer.applicant_name}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Your Response</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: "accept", label: "Accept", icon: Check, color: "bg-green-100 border-green-500 text-green-700" },
                    { value: "reject", label: "Reject", icon: X, color: "bg-red-100 border-red-500 text-red-700" },
                    { value: "counter", label: "Counter", icon: MessageSquare, color: "bg-purple-100 border-purple-500 text-purple-700" },
                    { value: "request_info", label: "Need Info", icon: AlertCircle, color: "bg-amber-100 border-amber-500 text-amber-700" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setOfferResponse({ ...offerResponse, action: option.value })}
                      className={`p-3 rounded-lg border-2 flex items-center gap-2 transition-all ${
                        offerResponse.action === option.value
                          ? option.color
                          : "bg-white border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`offer-action-${option.value}`}
                    >
                      <option.icon className="w-4 h-4" />
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {offerResponse.action === "counter" && (
                <div>
                  <Label htmlFor="counter-amount">Counter Offer Amount (£)</Label>
                  <Input
                    id="counter-amount"
                    type="number"
                    placeholder="Enter amount"
                    value={offerResponse.counter_amount}
                    onChange={(e) => setOfferResponse({ ...offerResponse, counter_amount: e.target.value })}
                    className="mt-1"
                    data-testid="counter-amount-input"
                  />
                </div>
              )}
              
              <div>
                <Label htmlFor="offer-notes">Notes (optional)</Label>
                <Textarea
                  id="offer-notes"
                  placeholder="Any message for the buyer or agent..."
                  value={offerResponse.notes}
                  onChange={(e) => setOfferResponse({ ...offerResponse, notes: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOfferModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleOfferResponse}
              disabled={actionLoading || !offerResponse.action}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-offer-response"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Response
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Upload Modal */}
      <Dialog open={uploadModal} onOpenChange={setUploadModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-600" />
              Upload {uploadItem?.label}
            </DialogTitle>
            <DialogDescription>
              {uploadItem?.description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-emerald-400 transition-colors">
              <input
                type="file"
                id="file-upload"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                data-testid="file-input"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                {selectedFile ? (
                  <div className="space-y-2">
                    <FileCheck className="w-12 h-12 text-emerald-500 mx-auto" />
                    <p className="font-medium text-slate-700">{selectedFile.name}</p>
                    <p className="text-sm text-slate-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={(e) => { e.preventDefault(); setSelectedFile(null); }}
                    >
                      Choose Different File
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 text-slate-300 mx-auto" />
                    <p className="font-medium text-slate-600">Click to select a file</p>
                    <p className="text-sm text-slate-400">PDF, JPG, PNG or DOC (max 10MB)</p>
                  </div>
                )}
              </label>
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-xs text-blue-700">
                <strong>Tip:</strong> Make sure your document is clear and all text is readable. 
                For IDs, ensure all corners are visible.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadModal(false); setSelectedFile(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleFileUpload}
              disabled={uploading || !selectedFile}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-black text-white py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Goodacres Residential. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SellerDashboard;
